import { useState, useCallback, useEffect } from 'react'
import AddBottomButton from '../../../components/Add/AddBottomButton'
import AddModel from '../../../components/Add/AddModel'
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs'
import AddInputs from '../../../components/Add/AddInputs'
import CustomRadioButton from '../../../components/buttons/CustomRadioButton'
import useApi from '../../../services/useApi'
import { fetchAuthSession } from '@aws-amplify/auth'
import { useSelector } from 'react-redux'
import CustomModal from '../../../components/CustomModal'
import PinVerificationModel from '../../../components/Models/PinVerificationModel'
import { showToast } from '../../../components/toastMessage/toastSlice'
import { useDispatch } from 'react-redux'
import ETopUpTransactionReceipt from '../../Profile/RequestRaised/ETopUpTransactionReciept'

function Recharge({ testing = false }: { testing?: boolean } = {}) {
  const dispatch = useDispatch()
  const { getAPI, PostAPI } = useApi();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [telecomData, setTelecomData] = useState<any>({
    name: "",
    min_recharge_amount: 0,
    max_recharge_amount: null,
  });
  const [wallets, setWallets] = useState<any>([]);
  const [selectedWallet, setSelectedWallet] = useState<string>("");
  const [prevFirstTwoDigits, setPrevFirstTwoDigits] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorMessage, setErrorMessage] = useState("");
  const email = useSelector((state: any) => state.userEmail.email);
  const [showModal, setShowModal] = useState(false);
  const [isTransactionReceipt, setIsTransactionReceipt] = useState(false);
  const [responseData, setResponseData] = useState<any>({});
  const [isVerificationModelOpen, setIsVerificationModelOpen] = useState(false);
  const [pin, setPin] = useState<string[]>(new Array(4).fill(""));
  const [selectedDropDownValue, setSelectedDropDownValue] = useState<string>("");
  const [isDontAskAgainChecked, setIsDontAskAgainChecked] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const inputs = {
    "": {
      "Phone Number": {
        required: true,
        disabled: false,
        id: `phone_number`,
        type: "phone_number_no_verify",
        tabIndex: 0,
      },
      "Amount": {
        required: true,
        disabled: false,
        id: "amount",
        type: "currency",
        tabIndex: 1,
        min: telecomData?.min_recharge_amount || 0,
        max: telecomData?.max_recharge_amount || null,
      },
      ...(telecomData?.name && {
        "Telecom": {
          required: false,
          disabled: true,
          id: "telecom",
          type: "input",
          tabIndex: 2,
        }
      })
    }
  }

  const getUserId = async () => {
    try {
      if (testing) {
        setWallets([
          { type: 'etopup', name: 'E Top up Stocks', enabled: true },
          { type: 'wallet2', name: 'Wallet 2', enabled: false },
        ]);
        setButtonDisabled(false);
        return;
      }
      let res = await getAPI(`admins/email/${encodeURIComponent(email)}`);
      getWalletTypes(res?.data?.data?.id)
    } catch {
      console.error('error')
    }
  }

  const getWalletTypes = async (id: string) => {
    try {
      if (testing) {
        setWallets([
          { type: 'etopup', name: 'E Top up Stocks', enabled: true },
          { type: 'wallet2', name: 'Wallet 2', enabled: false },
        ]);
        setButtonDisabled(false);
        return;
      }
      let res = await getAPI(`wallets/all/${id}`);
      if (!res.error) {
        setWallets(res?.data?.data?.wallets)
        setButtonDisabled(false);
      } else {
        console.error('error')
      }
    } catch {
      console.error('error')
    }
  }

  const debouncedApiCall = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (countryCode: string, firstTwoDigits: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          // Add debug log for test
          console.log('API CALL countryCode:', String(countryCode), 'firstTwoDigits:', String(firstTwoDigits));
          if (testing) {
            setTelecomData({
              name: 'MTN',
              min_recharge_amount: 10,
              max_recharge_amount: 1000,
            });
            setErrors({});
            getUserId();
            return;
          }
          const res = await getAPI(`recharges/mno?country_code=${encodeURIComponent('+' + countryCode)}&phone_number=${firstTwoDigits}`);
          if (res?.error) {
            setErrors({ phone_number: res.data?.data?.message });
            setTelecomData({});
            setWallets([])
            setButtonDisabled(true);
          } else {
            setErrors({});
              setTelecomData(res?.data?.data);
            getUserId();
          }
        }, 300);
      };
    })(),
    [testing]
  );

  const handleInputChange = async (id: string, value: any) => {
    if (id === 'isDontAskAgainChecked') {
      setIsDontAskAgainChecked(value)
    }
    if (id === 'pin_interval') {
      setSelectedDropDownValue(value)
      setErrors((prev) => {
        const { pin_interval, ...rest } = prev;
        return rest;
      });
    }
    if (id === 'country_code') {
      setPhoneNumber('')
      setCountryCode(value)
      // setErrors({});
    }
    if (id === 'phone_number') {
      setPhoneNumber(value)
      if (value === '') {
        setErrors({});
      } else if (
        errors.phone_number !==
        "The telecom could not be detected based on the phone number you entered. Please try again."
      ) {
        setErrors({});
      }
      const phoneWithoutCountryCode = value.slice(countryCode.length);

      if(phoneWithoutCountryCode.length < 2){
        setPrevFirstTwoDigits('')
      }
      // Handle the case when a phone number is pasted and country code is empty
      if (value.length > 0 && countryCode === '') {
        // Extract the first two digits as potential country code
        const potentialCountryCode = value.slice(0, 2);
        setCountryCode(potentialCountryCode);
        const firstTwoDigits = value.slice(2, 4);
        if (firstTwoDigits.length === 2 && firstTwoDigits !== prevFirstTwoDigits) {
          setPrevFirstTwoDigits(firstTwoDigits);
          debouncedApiCall(potentialCountryCode, firstTwoDigits);
        }
      }
      // Only proceed if the value is longer than the country code
      else if ((value.length > countryCode.length) && countryCode !== '') {
        
        const firstTwoDigits = phoneWithoutCountryCode.slice(0, 2);
        const countryCodeLength = countryCode.length;
        const countryCodeFromValue = value.slice(0, countryCodeLength);
        if (firstTwoDigits.length === 2 && firstTwoDigits !== prevFirstTwoDigits && countryCodeFromValue === countryCode) {
          setPrevFirstTwoDigits(firstTwoDigits);
          debouncedApiCall(countryCode, firstTwoDigits);
        } else if (countryCodeFromValue !== countryCode) {
          setWallets([])
          setTelecomData({});
        }
      }
    }
    if (id === 'amount') {
      if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
        setAmount(value);
        setErrors((prev) => {
          const { amount, ...rest } = prev;
          return rest;
        });
      }
    }
    if (id === 'wallet') {
      setSelectedWallet(value);
      setErrors({});
    }
  };

  const handleWalletChange = (walletId: string) => {
    setSelectedWallet(walletId);
    setErrors((prev) => {
      const { wallet, ...rest } = prev;
      return rest;
    });
  };

  const validations = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!phoneNumber) {
      newErrors.phone_number = "This field is required";
    }

    if (!amount) {
      newErrors.amount = "This field is required";
    } else {
      if (errors.phone_number === "The telecom could not be detected based on the phone number you entered. Please try again.") {
        newErrors.phone_number = errors.phone_number;
      }
      if (telecomData.min_recharge_amount && Number(amount) < telecomData.min_recharge_amount) {
        newErrors.amount = `Amount must be greater than or equal to AFA ${telecomData.min_recharge_amount}${!String(telecomData.min_recharge_amount).includes('.') ? '.00' : ''}`;
      } else if (telecomData.max_recharge_amount && Number(amount) > telecomData.max_recharge_amount) {
        newErrors.amount = `Maximum amount is AFA ${telecomData.max_recharge_amount}${!String(telecomData.max_recharge_amount).includes('.') ? '.00' : ''}`;
      }
    }

    if (wallets.length > 0 && !selectedWallet) {
      newErrors.wallet = "Please select a wallet";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    } else {
      setShowModal(true)
    }
  };

  const handleClickVerify = async (isPin: boolean) => {
    setVerifyLoading(true);
    setErrors((prev) => {
      const { pin_interval, ...rest } = prev;
      return rest;
    });
    try {
      if (isPin) {
        // A prior "Invalid PIN" message is intentionally left on screen here (rather than
        // cleared unconditionally) so it stays visible alongside the interval error below -
        // the PIN itself hasn't changed since that last failed verification.
        let hasValidationError = false;
        if (pin.includes("")) {
          setErrorMessage("This field is required");
          hasValidationError = true;
        }
        if (isDontAskAgainChecked && selectedDropDownValue === "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            pin_interval: "This field is required",
          }));
          hasValidationError = true;
        }
        if (hasValidationError) {
          setVerifyLoading(false);
          return;
        }
      }

      const phoneWithoutCountryCode = phoneNumber.slice(countryCode.length);
      const payload: any = {
        country_code: '+' + countryCode,
        phone_number: phoneWithoutCountryCode,
        amount: Number(amount),
        wallet_type: selectedWallet || "etopup",
      };

      // Only include PIN-related fields if PIN verification is required
      if (isPin) {
        payload.pin = pin.join("");
        if (isDontAskAgainChecked) {
          payload.pin_interval = selectedDropDownValue;
        }
      }

      const response = await PostAPI('recharges/', payload);
      if (response.error) {
        if (response?.data?.response?.data?.message === "Invalid PIN.") {
          setErrorMessage(response?.data?.response?.data?.message || "Verification failed");
        } else {
          setIsVerificationModelOpen(false);
          setPin(new Array(4).fill(""));
          setSelectedDropDownValue("");
          setErrorMessage("")
          setShowModal(false);
          setIsDontAskAgainChecked(false);
          dispatch(showToast({
            type: "error",
            message: response?.data?.response?.data?.message || "Something went wrong",
          }));
        }
      } else {
        setResponseData(response?.data?.data)
        setIsVerificationModelOpen(false);
        setShowModal(false);
        setIsTransactionReceipt(true);
        setPin(new Array(4).fill(""));
        setSelectedDropDownValue("");
        setIsDontAskAgainChecked(false);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleClickDone = () => {
    setResponseData({});
    setIsTransactionReceipt(false);
    setPhoneNumber("+93");
    setCountryCode("+93")
    setAmount("");
    setPin(new Array(4).fill(""));
    setTelecomData({});
    setWallets([]);
    setSelectedWallet("");
    setErrors({});
    setShowModal(false);
  };

  // Find enabled and disabled wallet options
  const getWalletOptions = () => {
    return wallets.map((wallet: any) => ({
      id: wallet.type,
      label: wallet.name,
      testID: `wallet-${wallet.type}`
    }));
  };

  const getDisabledWallets = () => {
    return wallets
      .filter((wallet: any) => !wallet.enabled)
      .map((wallet: any) => wallet.type);
  };

  const handleSubmitPopup = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Check if PIN verification is needed based on expiry timestamp
      const user = await fetchAuthSession({ forceRefresh: true });
      const timestamp = user?.tokens?.idToken?.payload["custom:pin_expiry_timestamp"];

      if (timestamp) {
        if (typeof timestamp === 'string' || typeof timestamp === 'number') {
          const inputDate = new Date(timestamp);
          const currentDate = new Date();
          if (inputDate < currentDate) {
            // PIN expired, show verification modal
            setShowModal(false);
            setIsVerificationModelOpen(true);
          } else {
            // PIN still valid, proceed without verification
            await handleClickVerify(false);
          }
        } else {
          // Invalid timestamp, show verification modal
          setShowModal(false);
          setIsVerificationModelOpen(true);
        }
      } else {
        // No timestamp, show verification modal
        setShowModal(false);
        setIsVerificationModelOpen(true);
      }
    } catch (error) {
      console.error("Error checking PIN expiry:", error);
      // If there's an error, default to showing the verification modal
      setIsVerificationModelOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Breadcrumbs
        navPath={["Services", "Recharge"]}
        heading="Services"
        noNavigation
        links={["/services/recharge"]}
        hasAddPrivilege={true}
        list={true}
      />
      <AddModel>
        <AddInputs
          inputs={inputs}
          totalInputCount={3}
          inputValues={{
            phone_number: phoneNumber || "",
            amount: amount || "",
            telecom: telecomData.name || "",
          }}
          mapping={true}
          onInputChange={(id, value) => handleInputChange(id, value)}
          errors={errors}
        />
        {wallets.length > 0 && (
          <div className="mt-4">
            <h1 className="text-xl underline font-semibold mb-2">
              Wallet Type
            </h1>
            <CustomRadioButton
              options={getWalletOptions()}
              selected={selectedWallet}
              onChange={handleWalletChange}
              padding="py-3 pr-3"
              isDisabled={getDisabledWallets()}
            />
            {errors.wallet && (
              <p className="text-negative text-sm mt-1">{errors.wallet}</p>
            )}
          </div>
        )}
      </AddModel>
      <AddBottomButton
        buttonName="Submit"
        buttonWidth="w-[125px]"
        onClick={() => {
          validations()
        }}
        loading={false}
        disabled={buttonDisabled}
        testID="submit"
      />
      {showModal && (
        <CustomModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={'Are you sure you want to proceed with this recharge?'}
          subText={`You're about to recharge ${'+' + countryCode} ${phoneNumber.slice(countryCode.length)} with AFA ${Number(amount).toFixed(2)} using ${selectedWallet === 'etopup' ? 'E Top up Stocks' : 'Digital Wallet'} via ${telecomData.name}.`}
          primaryButtonText={'Yes'}
          secondaryButtonText={'No'}
          handelSubmit={handleSubmitPopup}
          buttonColor={'bg-p_primary'}
          buttonLoading={loading}
          reverseButton={false}
        />
      )}
      {isVerificationModelOpen && (
        <PinVerificationModel
          handleClose={() => {
            setIsVerificationModelOpen(false)
            setPin(new Array(4).fill(""));
            setIsDontAskAgainChecked(false);
            setSelectedDropDownValue("");
            setErrorMessage("");
            setErrors({})
          }}
          pin={pin}
          setPin={setPin}
          handleDropdownChange={(key, value) => {
            if (typeof key === 'string') {
              // @ts-ignore
              handleInputChange(key, value);
            }
          }}
          selectedDropDownValue={selectedDropDownValue}
          isDontAskAgainChecked={isDontAskAgainChecked}
          handleClickVerify={() => handleClickVerify(true)}
          verifyLoading={verifyLoading}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          dropdownError={errors?.pin_interval}
        />
      )}
      {isTransactionReceipt && (
        <ETopUpTransactionReceipt
          details={responseData}
          handleClickDone={handleClickDone}
          recharge={true}
        />
      )}
    </div>
  );
}

export default Recharge